
import { Component, Vue, Watch } from "vue-property-decorator";
import { API_ROUTES } from "@/utils/API";
import { TTab } from "@/models/tabs";
import {
  EXPERT_EMPLOYEE_TABS_VALUES,
  EXPERT_EMPLOYEE_TABS,
  EXPERT_EMPLOYEE_TAB,
  EMPLOYEE_EMPLOYMENT_STATUS_EMPLOYED,
  EMPLOYEE_EMPLOYMENT_STATUS_LEAVING,
  EMPLOYEE_EMPLOYMENT_STATUS_LEAVER,
} from "@/constants/compliance";
import {
  API_TExpert_Employee,
  API_TExpert_Employee_Filter,
  API_TExpert_Employees,
} from "@/models/cases_expert_employee";

const TABLE_INITIAL_LIMIT = 20;
const TABLE_INITIAL_OFFSET = 0;
const TABLE_INITIAL_ORDER = "asc";
const TABLE_INITIAL_ORDER_BY = "name";

type ParamsKeyObject = {
  [key: string]: any;
};

@Component({
  components: {
    Tabs: () => import("@/components/Tabs/Tabs.vue"),
    ExpertEmployeesTable: () =>
      import("@/modules/Services/Components/Table/ExpertEmployeesTable.vue"),
    TableSearchbar: () => import("@/components/TableSearchbar/TableSearchbar.vue"),
  },
})
export default class ExpertEmployeesBrowse extends Vue {
  loading = false;

  loadingFilters = false;

  items: API_TExpert_Employee[] = [];

  filters: API_TExpert_Employee_Filter = {
    company_name: [],
    compliance_status: [],
    compliance_subscription: [],
    pending_tasks: [],
  };

  params: ParamsKeyObject = {
    limit: TABLE_INITIAL_LIMIT,
    order: TABLE_INITIAL_ORDER,
    orderBy: TABLE_INITIAL_ORDER_BY,
    offset: TABLE_INITIAL_OFFSET,
    search: "",
    list_name: EXPERT_EMPLOYEE_TAB,
  };

  total = 0;

  async mounted() {
    await this.fetchTableFilters();
  }

  tabDigits = {
    all: 0,
    premium: 0,
  };

  showOnlyActiveEmployeesSwitcher = false;

  @Watch("showOnlyActiveEmployeesSwitcher")
  async showOnlyActiveEmployeesSwitcherChanged() {
    this.items = [];

    this.params.offset = 0;

    const working_status = this.showOnlyActiveEmployeesSwitcher
      ? [EMPLOYEE_EMPLOYMENT_STATUS_EMPLOYED, EMPLOYEE_EMPLOYMENT_STATUS_LEAVING]
      : [
          EMPLOYEE_EMPLOYMENT_STATUS_EMPLOYED,
          EMPLOYEE_EMPLOYMENT_STATUS_LEAVING,
          EMPLOYEE_EMPLOYMENT_STATUS_LEAVER,
        ];

    Object.assign(this.params, {
      working_status,
    });

    await this.fetchTableEmployees({ params: this.params, isLimit: true });
  }

  async fetchTableEmployees({ params, isLimit }: { params: ParamsKeyObject; isLimit: boolean }) {
    try {
      this.loading = true;

      const { data } = await this.axios.get<API_TExpert_Employees>(
        API_ROUTES.EXPERT_EMPLOYEES_BY_FILTER(),
        { params }
      );

      this.total = data.total_count;

      if (!data.employees.length && !isLimit) return (this.items = []);

      const items = data.employees;

      this.items = isLimit ? [...this.items, ...items] : data.employees;

      this.tabDigits.all = data.employees_tabs.all_count || 0;
      this.tabDigits.premium = data.employees_tabs.premium_count || 0;
    } finally {
      this.loading = false;
    }
  }

  async fetchTableFilters() {
    try {
      this.loadingFilters = true;

      const { data } = await this.axios.get<API_TExpert_Employee_Filter>(
        API_ROUTES.EXPERT_EMPLOYEES_FILTERS()
      );

      if (!data) return;

      this.filters = data;
    } finally {
      this.loadingFilters = false;
    }
  }

  async updateQuery(payload?: { params: ParamsKeyObject; isLimit: boolean }) {
    this.params.offset = 0;

    const requestParams = payload ?? { params: this.params, isLimit: false };

    await this.fetchTableEmployees(requestParams);
  }

  tabNames: string[] = EXPERT_EMPLOYEE_TABS_VALUES;

  currentTabName = this.tabNames[0];

  tableKey = 0;

  get tabs(): TTab[] {
    return this.tabNames.map((name) => ({
      label: EXPERT_EMPLOYEE_TABS[name].toUpperCase(),
      isActive: this.currentTabName === name,
      digit: this.tabDigits[name],
      action: () => {
        this.currentTabName = name;
        this.tableKey += 1;
      },
    }));
  }

  @Watch("currentTabName", { immediate: true })
  async currentTabNameChanged(value: string) {
    this.params = {
      limit: TABLE_INITIAL_LIMIT,
      order: TABLE_INITIAL_ORDER,
      orderBy: TABLE_INITIAL_ORDER_BY,
      offset: TABLE_INITIAL_OFFSET,
      search: this.params.search,
      list_name: value,
    };

    await this.fetchTableEmployees({ params: this.params, isLimit: false });
  }
}
